import {NOVA_INVOCATION_COLUMN_WIDTH} from '../../../shared/components/nova-invocation/styleTokens';
import {isNovaInvocationEnabled} from '../../../shared/components/nova-invocation/novaInvocationGate';

export interface EyeColumn {
	name: string;
	checked: boolean;
	translationId?: string;
}

/**
 * The two decisions the Sprints page makes about its Nova column, kept out of the page itself.
 *
 * Both answer the same question - is there a Nova column right now - and both have to answer it the
 * same way. Apart, one is the width budget and the other is the view-options menu, and a page where
 * they disagree either hides the option on a column it is drawing or reserves room for a column it
 * is not.
 */

/**
 * Whether the page is drawing a Nova column at all: the viewer may act on Nova, and has not turned
 * the column off.
 *
 * The one effective answer, which everything else on this page asks and nothing re-derives. Every
 * surface - the table's own column list, each row's control, the backlog cards, the width budget
 * and the sprint headers - has to agree, and the two halves are easy to get apart: a page that
 * draws the column without the gate shows a viewer a control the server would refuse and reserves
 * no width for it, and a page that gates the width but not the column draws a header for a feature
 * the account does not have.
 */
export const isNovaColumnShown = (showNovaColumn: boolean): boolean => isNovaInvocationEnabled() && showNovaColumn;

/**
 * The room the Nova control takes out of the task cell's budget.
 *
 * The control sits inside that cell's own leading cluster rather than in a column of its own, so its
 * width has to be subtracted from what the cell is given. Left out, the cell keeps the whole width
 * it had before the column existed and the row draws wider than the container it sits in - a
 * horizontal scrollbar and a truncated task name, every time the column is shown.
 */
export const novaColumnWidth = (showNovaColumn: boolean): number =>
	isNovaColumnShown(showNovaColumn) ? NOVA_INVOCATION_COLUMN_WIDTH : 0;

/**
 * The page's columns, with the Nova option appended for the view-options menu.
 *
 * Listed only where the feature is on: an account without Nova has no column for the option to
 * speak about, and an option that governs nothing is worse than no option at all. Appended rather
 * than stored with the others because the rest are a saved column set this page persists, and Nova
 * is not one of them - it is a property of the page.
 */
export const withNovaEyeOption = <T extends {name: string}>(
	availableColumns: ReadonlyArray<T>,
	showNovaColumn: boolean
): ReadonlyArray<T | EyeColumn> =>
	isNovaInvocationEnabled()
		? [...availableColumns, {name: 'nova', checked: showNovaColumn, translationId: 'nova_invocation.column.header'}]
		: availableColumns;

/**
 * The key the column's view option is remembered under.
 *
 * A property of this page rather than one of the column set it persists beside, so it is stored the
 * way the page's other view toggles are rather than folded into the saved columns.
 */
export const NOVA_COLUMN_STORAGE_KEY = 'project-sprints-show-nova-column';

/**
 * Whether to start with the column shown.
 *
 * Shown unless the person turned it off, which is the default every other Nova surface takes. Read
 * back on every mount, because a view option that forgets itself on a reload is one the person has
 * to turn off again on every visit - every other option in the same menu is remembered.
 */
export const readStoredNovaColumnPreference = (stored: string | null): boolean => {
	if (stored === null) {
		return true;
	}
	try {
		return !!JSON.parse(stored);
	} catch {
		// Anything unparseable is treated as never having been set, rather than as "off": a person
		// who has not chosen is shown the column.
		return true;
	}
};
